// React
import React, { memo } from 'react';
import PropTypes from 'prop-types';

// Components
import Dropdown from '../../msreact-kit/html-components/drop-down';
import Pagination from '../../msreact-kit/html-components/table/pagination';
import { dropDownData } from '../../msreact-kit/html-components/table/data';
import CustomToolTip from '../Shared/custom-tooltip';

// Data
import { COLUMNS, SHIPPING_TYPE_OPTIONS, SHIPMENT_MODE_OPTIONS } from './data';

// Css
import './index.scss';

const statusClassMap = {
    ENABLED: 'shipping-grid__status--enabled',
    DISABLED: 'shipping-grid__status--disabled'
};

const StatusBadge = ({ status, statusLabels }) => {
    const labels = {
        ENABLED: statusLabels?.enabled,
        DISABLED: statusLabels?.disabled
    };
    return (
        <span className={`shipping-grid__status ${statusClassMap[status] || ''}`}>
            {labels[status] || status}
        </span>
    );
};

const ActionCell = ({ row, onAction, tableContent }) => (
    <div className='shipping-grid__actions-cell'>
        <button
            className={`shipping-grid__actions-cell__btn shipping-grid__actions-cell__btn--delete`}
            title={tableContent?.action_edit}
            onClick={() => onAction('edit', row)}
        >
            <i className='a-icon boschicon-bosch-ic-edit' />
        </button>
        <button
            className={`shipping-grid__actions-cell__btn shipping-grid__actions-cell__btn--delete`}
            title={tableContent?.action_delete}
            onClick={() => onAction('delete', row)}
        >
            <i className='a-icon boschicon-bosch-ic-delete' />
        </button>
    </div>
);

const TruncatedCell = ({ id, value }) => (
    <CustomToolTip
        id={id}
        tooltipData={value}
        width='dynamic'
        className='shipping-grid__cell-tooltip'
    >
        <div className='text-ellipsis' id={id}>
            {value}
        </div>
    </CustomToolTip>
);

TruncatedCell.propTypes = {
    id: PropTypes.string.isRequired,
    value: PropTypes.string
};

const ShippingRulesGrid = memo(
    ({
        data = [],
        onAction,
        tableContent,
        statusLabels,
        currentPage,
        pageSize,
        totalPages,
        onPageChange
    }) => {
        const TD = 'shipping-grid__table__td';
        const TD_TRUNC = `${TD} ${TD}--ellipsis`;

        const handlePerPage = (event) => {
            onPageChange(0, Number(event.value.value));
        };

        const columns = COLUMNS.map((col) => ({
            key: col.key,
            label: tableContent?.columns?.[col.key] || ''
        }));

        return (
            <div>
                <div className='shipping-grid__table-wrapper'>
                    <table className='shipping-grid__table'>
                        <thead>
                            <tr>
                                {columns.map((col) => (
                                    <th key={col.key} className='shipping-grid__table__th'>
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.length === 0 ? (
                                <tr className='shipping-grid__table__empty-row'>
                                    <td
                                        colSpan={columns.length}
                                        className='shipping-grid__table__empty'
                                    >
                                        {tableContent?.empty}
                                    </td>
                                </tr>
                            ) : (
                                data.map((row) => (
                                    <tr key={row.shippingRuleId} className='shipping-grid__table__row'>
                                        <td className={TD_TRUNC}>
                                            <TruncatedCell id={`td-${row.shippingRuleId}-name`} value={row.shippingRuleName} />
                                        </td>
                                        <td className={TD_TRUNC}>
                                            <TruncatedCell id={`td-${row.shippingRuleId}-sac`} value={row.sacCode} />
                                        </td>
                                        <td className={TD_TRUNC}>
                                            <TruncatedCell id={`td-${row.shippingRuleId}-cost`} value={row.shippingCharge} />
                                        </td>
                                        <td className={TD_TRUNC}>
                                            <TruncatedCell id={`td-${row.shippingRuleId}-currency`} value={row.currency} />
                                        </td>
                                        <td className={TD_TRUNC}>
                                            <TruncatedCell id={`td-${row.shippingRuleId}-metrics`} value={SHIPPING_TYPE_OPTIONS?.find(option => option.value === row.shippingType)?.label || ''} />
                                        </td>
                                        <td className={TD_TRUNC}>
                                            <TruncatedCell id={`td-${row.shippingRuleId}-mode`} value={SHIPMENT_MODE_OPTIONS?.find(option => option.value === row.shipmentMode)?.label || ''} />
                                        </td>
                                        <td className={TD}>
                                            <StatusBadge
                                                status={row.isActive ? 'ENABLED' : 'DISABLED'}
                                                statusLabels={statusLabels}
                                            />
                                        </td>
                                        <td className={TD}>
                                            <ActionCell
                                                row={row}
                                                onAction={onAction}
                                                tableContent={tableContent}
                                            />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 0 && (<div className='shipping-grid__pagination-bar'>
                    <div className='shipping-grid__pagination-bar__items'>
                        <span className='shipping-grid__pagination-bar__items__title'>
                            {tableContent?.items_per_page} :
                        </span>
                        <div className='shipping-grid__pagination-bar__items__drop'>
                            <Dropdown
                                {...dropDownData}
                                handleChange={handlePerPage}
                                value={pageSize}
                            />
                        </div>
                    </div>
                    <div className='shipping-grid__pagination-bar__pagination'>
                        <Pagination
                            className='pagination-bar'
                            currentPage={currentPage}
                            paginationData={{ totalPages }}
                            onPageChange={(page) => onPageChange(page, pageSize)}
                        />
                    </div>
                </div>)}
            </div>
        );
    }
);

StatusBadge.propTypes = {
    status: PropTypes.string.isRequired,
    statusLabels: PropTypes.object
};

ActionCell.propTypes = {
    row: PropTypes.object.isRequired,
    onAction: PropTypes.func.isRequired,
    tableContent: PropTypes.object
};

ShippingRulesGrid.propTypes = {
    data: PropTypes.array,
    onAction: PropTypes.func.isRequired,
    tableContent: PropTypes.object,
    statusLabels: PropTypes.object,
    currentPage: PropTypes.number,
    pageSize: PropTypes.number,
    totalPages: PropTypes.number,
    onPageChange: PropTypes.func.isRequired
};

ShippingRulesGrid.defaultProps = { data: [] };

export default ShippingRulesGrid;
